import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Stack,
    Grid,
    CircularProgress,
    IconButton,
    Card,
    InputAdornment
} from '@mui/material';

import { v4 as uuidv4 } from 'uuid';

import {
    setDoc,
    doc,
    getDocs,
    getDoc,
    collection,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where
} from 'firebase/firestore';

import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';

import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';

const NextClass = () => {

    const uid = auth.currentUser?.uid;

    const [schoolId, setSchoolId] = useState(null);

    const [open, setOpen] = useState(false);

    const [formData, setFormData] = useState({
        current_class: '',
        next_class: '',
        tc_required: 'No',
        schoolId: ''
    });

    const [classes, setClasses] = useState([]);
    const [courseOptions, setCourseOptions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);


    // FETCH SCHOOL ID
    const fetchSchoolId = async () => {
        try {

            const userDoc = await getDoc(doc(db, "users", uid));

            if (userDoc.exists()) {

                const data = userDoc.data();
                setSchoolId(data.schoolId);

                setFormData(prev => ({
                    ...prev,
                    schoolId: data.schoolId
                }));

            }

        } catch (error) {

            console.error(error);
            toast.error("Failed to fetch schoolId");

        }
    };


    // FETCH COURSES
    const fetchCourseOptions = async () => {

        if (!schoolId) return;

        try {

            const q = query(
                collection(db, 'courses'),
                where('schoolId', '==', schoolId),
                orderBy('sequence', 'asc')
            );

            const snapshot = await getDocs(q);

            const courseList = snapshot.docs.map(doc => doc.data().name);

            setCourseOptions(courseList);

        } catch (err) {

            console.error("Error fetching courses", err);

        }
    };


    // FETCH NEXT CLASSES
    const fetchClasses = async () => {

        if (!schoolId) return;

        try {

            const q = query(
                collection(db, 'nextclasses'),
                where('schoolId', '==', schoolId),
                orderBy('createdAt','asc')
            );

            const snapshot = await getDocs(q);

            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setClasses(data);

        } catch (error) {

            console.error(error);
            toast.error("Failed to fetch next class data");

        }
    };


    useEffect(() => {

        if (uid) {
            fetchSchoolId();
        }

    }, [uid]);


    useEffect(() => {

        if (schoolId) {

            fetchCourseOptions();
            fetchClasses();

        }

    }, [schoolId]);


    const handleOpen = () => setOpen(true);

    const handleClose = () => {

        setOpen(false);

        setFormData({
            current_class: '',
            next_class: '',
            tc_required: '',
            schoolId: schoolId
        });

        setEditMode(false);
        setEditId(null);
    };


    const handleChange = (e) => {

        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });

    };


    // ADD OR UPDATE
    const handleSubmit = async () => {

        const { current_class, next_class, tc_required } = formData;

        if (!current_class || !next_class || !tc_required) {

            toast.error("Please fill all required fields");
            return;

        }

        setLoading(true);

        try {

            if (editMode && editId) {

                await updateDoc(doc(db, "nextclasses", editId), {

                    ...formData,
                    updatedAt: new Date()

                });

                toast.success("Next class updated");

            } else {

                const id = uuidv4();

                await setDoc(doc(db, "nextclasses", id), {

                    ...formData,
                    createdAt: new Date()

                });

                toast.success("Next class added");

            }

            fetchClasses();
            // handleClose();

        } catch (err) {

            console.error(err);
            toast.error(err.message);

        }

        setLoading(false);

    };


    const handleEdit = (cls) => {

        setEditMode(true);
        setEditId(cls.id);

        setFormData({
            ...cls
        });

        setOpen(true);

    };


    const handleDelete = async (id) => {

        if (!window.confirm('Delete this record?')) return;

        try {

            await deleteDoc(doc(db, 'nextclasses', id));

            setClasses(prev => prev.filter(cls => cls.id !== id));

            toast.success('Deleted successfully');

        } catch (err) {

            console.error(err);

            toast.error('Delete failed');

        }

    };


    const filtered = classes.filter((cls) =>
        cls.current_class.toLowerCase().includes(searchTerm) ||
        cls.next_class.toLowerCase().includes(searchTerm)
    );


    const paginated = filtered.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );


    return (

        <Box>

            <Stack direction="row" justifyContent="space-between" mb={3}>

                <Button variant="contained" onClick={handleOpen}>
                    Add Next Class Definition
                </Button>

                <Typography variant="h5" fontWeight="bold">
                    Next Class Mappings
                </Typography>

            </Stack>

            <Card>

                <Box sx={{ p: 1 }}>

                    <TextField
                        size="small"
                        placeholder="Search class"
                        value={searchTerm}
                        onChange={(e) =>
                            setSearchTerm(e.target.value.toLowerCase())
                        }
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                    />

                </Box>

                <Paper>

                    <Table>

                        <TableHead>

                            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>

                                <TableCell>#</TableCell>
                                <TableCell>Actions</TableCell>
                                <TableCell>Current Class</TableCell>
                                <TableCell>Next Class</TableCell>
                                <TableCell>TC Required</TableCell>

                            </TableRow>

                        </TableHead>

                        <TableBody>

                            {paginated.length === 0 ? (

                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No entries yet
                                    </TableCell>
                                </TableRow>

                            ) : (

                                paginated.map((cls, index) => (

                                    <TableRow key={cls.id}>

                                        <TableCell>
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>

                                        <TableCell>

                                            <Stack direction="row" spacing={1}>

                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleEdit(cls)}
                                                >
                                                    <EditIcon />
                                                </Button>

                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => handleDelete(cls.id)}
                                                >
                                                    <DeleteIcon />
                                                </Button>

                                            </Stack>

                                        </TableCell>

                                        <TableCell>{cls.current_class}</TableCell>
                                        <TableCell>{cls.next_class}</TableCell>
                                        <TableCell>{cls.tc_required}</TableCell>

                                    </TableRow>

                                ))

                            )}

                        </TableBody>

                    </Table>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20]}
                        component="div"
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />

                </Paper>

            </Card>

            <Dialog open={open} fullWidth maxWidth="sm">

                <DialogTitle>
                    {editMode ? 'Edit Next Class' : 'Add Next Class'}
                </DialogTitle>

                <DialogContent dividers>

                    <Grid className="row" spacing={2}>

                        <Grid className="col-sm-3 mb-3">

                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Current Class"
                                name="current_class"
                                value={formData.current_class}
                                onChange={handleChange}
                            >

                                {courseOptions.map((name, index) => (

                                    <MenuItem key={index} value={name}>
                                        {name}
                                    </MenuItem>

                                ))}

                            </TextField>

                        </Grid>

                        <Grid className="col-sm-3 mb-3">

                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Next Class"
                                name="next_class"
                                value={formData.next_class}
                                onChange={handleChange}
                            >

                                {courseOptions.map((name, index) => (

                                    <MenuItem key={index} value={name}>
                                        {name}
                                    </MenuItem>

                                ))}

                            </TextField>

                        </Grid>

                        <Grid className="col-sm-3 mb-3">

                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="TC Required"
                                name="tc_required"
                                value={formData.tc_required}
                                onChange={handleChange}
                            >

                                <MenuItem value="Yes">Yes</MenuItem>
                                <MenuItem value="No">No</MenuItem>

                            </TextField>

                        </Grid>

                    </Grid>

                </DialogContent>

                <DialogActions>

                    <Button onClick={handleClose} color="error">
                        Cancel
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                    >

                        {loading
                            ? <CircularProgress size={24} />
                            : editMode ? 'Update' : 'Add'}

                    </Button>

                </DialogActions>

            </Dialog>

        </Box>
    );
};

export default NextClass;